export type Include<T, U extends T> = T extends U ? T : never;
// These are the main colours to be exposed / implemented by Straps components - they all have hover and or bg variants.
const semanticColors = [
  "primary",
  "secondary",
  "hyperlink",
  "brand",
  "warning",
  "positive",
  "negative",
  "accent-1",
  "accent-2",
  "white",
] as const;

export type SemanticColors = (typeof semanticColors)[number];

// These are the full set of colours including those without hover or bg variants, and the hover or bg variants themselves.
const additionalSemanticColors = [
  "primary-hover",
  "primary-highlight",
  "secondary-hover",
  "tertiary",
  "hyperlink-hover",
  "hyperlink-secondary",
  "hyperlink-secondary-hover",
  "body",
  "body-3",
  "brand-hover",
  "warning-hover",
  "warning-bg",
  "positive-hover",
  "positive-bg",
  "negative-hover",
  "negative-bg",
  "accent-1-hover",
  "accent-2-hover",
  "accent-3",
  "accent-3-hover",
] as const;

export type ExtendedSemanticColors =
  | SemanticColors
  | (typeof additionalSemanticColors)[number];

export const gradientSemanticColors = ["body-2"] as const;

export type AllSemanticColors =
  | ExtendedSemanticColors
  | (typeof gradientSemanticColors)[number];

// must be hardcoded for tailwind build
export const textColorClasses: {
  [K in ExtendedSemanticColors]: `text-straps-${K}`;
} = {
  primary: "text-straps-primary",
  "primary-highlight": "text-straps-primary-highlight",
  "primary-hover": "text-straps-primary-hover",
  secondary: "text-straps-secondary",
  "secondary-hover": "text-straps-secondary-hover",
  tertiary: "text-straps-tertiary",
  hyperlink: "text-straps-hyperlink",
  "hyperlink-hover": "text-straps-hyperlink-hover",
  "hyperlink-secondary": "text-straps-hyperlink-secondary",
  "hyperlink-secondary-hover": "text-straps-hyperlink-secondary-hover",
  body: "text-straps-body",
  "body-3": "text-straps-body-3",
  brand: "text-straps-brand",
  "brand-hover": "text-straps-brand-hover",
  warning: "text-straps-warning",
  "warning-hover": "text-straps-warning-hover",
  "warning-bg": "text-straps-warning-bg",
  positive: "text-straps-positive",
  "positive-hover": "text-straps-positive-hover",
  "positive-bg": "text-straps-positive-bg",
  negative: "text-straps-negative",
  "negative-hover": "text-straps-negative-hover",
  "negative-bg": "text-straps-negative-bg",
  "accent-1": "text-straps-accent-1",
  "accent-1-hover": "text-straps-accent-1-hover",
  "accent-2": "text-straps-accent-2",
  "accent-2-hover": "text-straps-accent-2-hover",
  "accent-3": "text-straps-accent-3",
  "accent-3-hover": "text-straps-accent-3-hover",
  white: "text-straps-white",
};

// catalog straps tokens via test setup
export const strapsTokens = [
  "--straps-font-sans",
  "--straps-radius-control",
  "--straps-radius-tag",
  "--straps-height-control",
  "--straps-button-text-size",
  "--straps-button-primary-fill-text",
  "--straps-button-primary-fill-text-hover",
  "--straps-not-implemented-hot-pink",

  // colors
  ...[
    ...semanticColors,
    ...additionalSemanticColors,
    ...gradientSemanticColors,
  ].map((color) => `--straps-${color}` as const),
] as const;
